import { resolveWeighbridgeNetWeight } from "./weighbridgeUtils";

const candidateSourceCache = new WeakMap();
const pickedValueCache = new WeakMap();
const parsedJsonCache = new Map();

const tryParseWeighbridgeJson = (value) => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue || !["{", "["].includes(trimmedValue[0])) {
    return null;
  }

  if (parsedJsonCache.has(trimmedValue)) {
    return parsedJsonCache.get(trimmedValue);
  }

  try {
    const parsedValue = JSON.parse(trimmedValue);
    parsedJsonCache.set(trimmedValue, parsedValue);
    return parsedValue;
  } catch (error) {
    parsedJsonCache.set(trimmedValue, null);
    return null;
  }
};

const appendWeighbridgeSource = (sources, seen, candidate) => {
  const parsedCandidate = tryParseWeighbridgeJson(candidate);
  const resolvedCandidate = parsedCandidate ?? candidate;

  if (Array.isArray(resolvedCandidate)) {
    resolvedCandidate.forEach((item) => appendWeighbridgeSource(sources, seen, item));
    return;
  }

  if (!resolvedCandidate || typeof resolvedCandidate !== "object") {
    return;
  }

  if (seen.has(resolvedCandidate)) {
    return;
  }

  seen.add(resolvedCandidate);
  sources.push(resolvedCandidate);
};

const appendNestedWeighbridgeSources = (
  sources,
  seen,
  candidate,
  depth = 0,
  maxDepth = 4,
) => {
  if (!candidate || depth > maxDepth) {
    return;
  }

  const parsedCandidate = tryParseWeighbridgeJson(candidate);
  const resolvedCandidate = parsedCandidate ?? candidate;

  if (Array.isArray(resolvedCandidate)) {
    resolvedCandidate.forEach((item) =>
      appendNestedWeighbridgeSources(sources, seen, item, depth + 1, maxDepth),
    );
    return;
  }

  if (!resolvedCandidate || typeof resolvedCandidate !== "object") {
    return;
  }

  appendWeighbridgeSource(sources, seen, resolvedCandidate);

  Object.values(resolvedCandidate).forEach((value) => {
    if (value && (typeof value === "object" || typeof value === "string")) {
      appendNestedWeighbridgeSources(sources, seen, value, depth + 1, maxDepth);
    }
  });
};

export const resolveWeighbridgeTicketRecord = (item = {}) => {
  const ticketCandidates = [
    item?.ticket,
    item?.tickets,
    item?.weigh_bridge_ticket,
    item?.weighbridge_ticket,
    item?.Weighbridge_ticket,
    item?.WeighbridgeTicket,
    item?.payload,
    item?.data,
    item?.result,
    item?.context,
    item?.raw_payload,
    item?.weighbridge_ticket_data,
  ];

  const nestedTicket = ticketCandidates.find(
    (candidate) =>
      candidate && typeof candidate === "object" && !Array.isArray(candidate),
  ) || null;

  const ticket = nestedTicket || item || {};

  const ticketStatusCandidates = [
    item?.ticket_status,
    item?.weigh_bridge_ticket_status,
    item?.weighbridge_ticket_status,
    item?.Weighbridge_ticket_status,
  ];

  const ticketStatus =
    ticketStatusCandidates.find(
      (candidate) =>
        candidate && typeof candidate === "object" && !Array.isArray(candidate),
    ) || {};

  const ticketTypeCandidates = [
    item?.ticket_type,
    item?.weigh_bridge_ticket_type,
    item?.weighbridge_ticket_type,
    item?.Weighbridge_ticket_type,
  ];

  const ticketType =
    ticketTypeCandidates.find(
      (candidate) =>
        candidate && typeof candidate === "object" && !Array.isArray(candidate),
    ) || {};

  const backendTicketId =
    ticket?.weighbridge_ticket_id ||
    ticket?.weigh_bridge_ticket_id ||
    item?.weighbridge_ticket_id ||
    item?.weigh_bridge_ticket_id ||
    "";

  return {
    ticket,
    ticketStatus,
    ticketType,
    backendTicketId,
  };
};

const isPresent = (value) => value !== null && value !== undefined && value !== "";

export const normalizeWeighbridgeText = (value) =>
  String(value || "")
    .trim()
    .replaceAll("_", " ")
    .toLowerCase();

const getWeighbridgeCandidateSources = (item = {}) => {
  if (item && typeof item === "object" && candidateSourceCache.has(item)) {
    return candidateSourceCache.get(item);
  }

  const { ticket, ticketStatus, ticketType } = resolveWeighbridgeTicketRecord(item);
  const sources = [];
  const seen = new Set();
  const queue = [
    item,
    ticket,
    item?.tickets,
    ticketStatus,
    ticketType,
    item?.payload,
    item?.data,
    item?.result,
    item?.context,
    item?.raw_payload,
    item?.weighbridge_ticket_data,
    item?.goods_received,
    item?.goods_received?.goods_received,
    item?.delivery_note,
    item?.delivery_note?.delivery_note,
    item?.raw,
    item?.raw?.tickets,
    item?.raw?.payload,
    item?.raw?.data,
    item?.raw?.result,
    item?.raw?.context,
    item?.raw?.raw_payload,
    item?.raw?.ticket,
    item?.raw?.weigh_bridge_ticket,
    item?.raw?.weighbridge_ticket,
    item?.raw?.goods_received,
    item?.raw?.goods_received?.goods_received,
    item?.raw?.delivery_note,
    item?.raw?.delivery_note?.delivery_note,
    ticket?.raw_payload,
    ticket?.payload,
    ticket?.data,
    ticket?.result,
    ticket?.context,
    ticket?.weighbridge_ticket_data,
  ];

  queue.forEach((candidate) => appendNestedWeighbridgeSources(sources, seen, candidate));

  const nestedCandidateKeys = [
    "ticket",
    "tickets",
    "weigh_bridge_ticket",
    "weighbridge_ticket",
    "Weighbridge_ticket",
    "WeighbridgeTicket",
    "payload",
    "data",
    "result",
    "context",
    "raw",
    "raw_payload",
    "weighbridge_ticket_data",
    "goods_received",
    "delivery_note",
  ];

  for (let index = 0; index < sources.length; index += 1) {
    const source = sources[index];

    nestedCandidateKeys.forEach((key) => {
      appendNestedWeighbridgeSources(sources, seen, source?.[key]);
    });
  }

  if (item && typeof item === "object") {
    candidateSourceCache.set(item, sources);
  }

  return sources;
};

export const pickWeighbridgeValue = (item = {}, keys = [], fallback = "") => {
  const normalizedKeys = Array.isArray(keys) ? keys : [keys];
  const cacheKey = `${normalizedKeys.join("|")}::${String(fallback)}`;

  if (item && typeof item === "object") {
    const itemCache = pickedValueCache.get(item);

    if (itemCache?.has(cacheKey)) {
      return itemCache.get(cacheKey);
    }
  }

  const candidateSources = getWeighbridgeCandidateSources(item);
  let resolvedValue = fallback;

  for (const source of candidateSources) {
    for (const key of normalizedKeys) {
      const value = source?.[key];

      if (isPresent(value)) {
        resolvedValue = value;
        if (item && typeof item === "object") {
          const itemCache = pickedValueCache.get(item) || new Map();
          itemCache.set(cacheKey, resolvedValue);
          pickedValueCache.set(item, itemCache);
        }
        return resolvedValue;
      }
    }
  }

  if (item && typeof item === "object") {
    const itemCache = pickedValueCache.get(item) || new Map();
    itemCache.set(cacheKey, resolvedValue);
    pickedValueCache.set(item, itemCache);
  }

  return resolvedValue;
};

export const resolveWeighbridgeTicketPrefillData = (item = {}) => {
  if (!item) {
    return {};
  }

  const sourceItem =
    item?.raw && typeof item.raw === "object" && !Array.isArray(item.raw)
      ? item.raw
      : item;
  const { ticket, ticketStatus, ticketType, backendTicketId } =
    resolveWeighbridgeTicketRecord(sourceItem);
  const branchLocation =
    ticket?.branch_location ||
    sourceItem?.branch_location ||
    sourceItem?.Branch_location ||
    {};

  const firstWeight = pickWeighbridgeValue(
    sourceItem,
    [
      "first_weight",
      "parsed_first_weight",
      "tare_weight",
      "tare",
      "weight_1",
      "weight1",
      "firstWeight",
      "tareweight",
      "tare_weight_kg",
    ],
    item?.first_weight || "",
  );
  const secondWeight = pickWeighbridgeValue(
    sourceItem,
    [
      "second_weight",
      "parsed_second_weight",
      "gross_weight",
      "gross",
      "weight_2",
      "weight2",
      "secondWeight",
      "grossweight",
      "gross_weight_kg",
    ],
    item?.second_weight || "",
  );
  const rawNetWeight = pickWeighbridgeValue(
    sourceItem,
    [
      "net_weight",
      "parsed_net_weight",
      "nett_weight",
      "net",
      "nett",
      "netWeight",
      "netweight",
      "net_weight_kg",
    ],
    item?.net_weight || "",
  );
  const branchArea =
    pickWeighbridgeValue(sourceItem, ["branch_area"], "") ||
    branchLocation?.branch_area ||
    branchLocation?.area ||
    "";
  const provinceName =
    pickWeighbridgeValue(sourceItem, ["province_name"], "") ||
    branchLocation?.province_name ||
    branchLocation?.province?.province_name ||
    "";
  const explicitBranchName =
    pickWeighbridgeValue(sourceItem, ["branch_location_name"], "") ||
    branchLocation?.branch_location_name ||
    branchLocation?.location_name ||
    branchLocation?.branch_name ||
    "";

  return {
    weighbridge_ticket_id:
      item?.weighbridge_ticket_id ||
      item?.weigh_bridge_ticket_id ||
      backendTicketId ||
      sourceItem?.id ||
      "",
    weigh_bridge_ticket_number:
      item?.weigh_bridge_ticket_number ||
      pickWeighbridgeValue(
        sourceItem,
        ["weigh_bridge_ticket_number", "weighbridge_ticket_number", "ticket_number"],
        "",
      ),
    weigh_bridge_ticket_reference_no:
      item?.weigh_bridge_ticket_reference_no ||
      pickWeighbridgeValue(
        sourceItem,
        ["weigh_bridge_ticket_reference_no", "weighbridge_ticket_reference_no", "reference_no"],
        "",
      ),
    external_weigh_bridge_ticket_number:
      item?.external_weigh_bridge_ticket_number ||
      pickWeighbridgeValue(
        sourceItem,
        ["external_weigh_bridge_ticket_number", "external_ticket_number"],
        "",
      ),
    truck_driver_name:
      item?.truck_driver_name ||
      pickWeighbridgeValue(
        sourceItem,
        [
          "truck_driver_name",
          "driver_name",
          "driver",
          "driver_full_name",
          "truck_driver",
          "driver_names",
          "driverName",
          "driver_fullname",
          "truck_driver_full_name",
        ],
        "",
      ),
    transporter_name:
      item?.transporter_name ||
      pickWeighbridgeValue(
        sourceItem,
        [
          "transporter_name",
          "haulier_name",
          "haulier",
          "transporter",
          "customer_haulier",
          "hauler_name",
        ],
        "",
      ),
    truck_licence_plate:
      item?.truck_licence_plate ||
      pickWeighbridgeValue(
        sourceItem,
        [
          "truck_licence_plate",
          "truck_license_plate",
          "vehicle_number",
          "horse_reg",
          "horse_reg_no",
          "truck_plate",
          "plate_number",
          "registration_number",
          "vehicle_reg_no",
          "vehicle_registration_number",
        ],
        "",
      ),
    trailer_one:
      item?.trailer_one ||
      pickWeighbridgeValue(
        sourceItem,
        ["trailer_one", "trailer_reg_one", "trailer1", "trailer_one_no", "trailer_no_1"],
        "",
      ),
    trailer_two:
      item?.trailer_two ||
      pickWeighbridgeValue(
        sourceItem,
        ["trailer_two", "trailer_reg_two", "trailer2", "trailer_two_no", "trailer_no_2"],
        "",
      ),
    comments:
      item?.comments ||
      pickWeighbridgeValue(sourceItem, ["comments", "comment"], ""),
    first_weight: firstWeight,
    second_weight: secondWeight,
    net_weight: resolveWeighbridgeNetWeight(
      item?.net_weight || rawNetWeight,
      firstWeight,
      secondWeight,
    ),
    time_in:
      item?.time_in ||
      pickWeighbridgeValue(sourceItem, ["time_in", "weighing_time_in", "date_in", "created_on"], ""),
    time_out:
      item?.time_out ||
      pickWeighbridgeValue(sourceItem, ["time_out", "weighing_time_out", "date_out"], ""),
    created_on:
      item?.created_on ||
      pickWeighbridgeValue(sourceItem, ["created_on", "created_at", "inserted_at", "weighed_at"], ""),
    branch_location_id:
      item?.branch_location_id ||
      pickWeighbridgeValue(sourceItem, ["branch_location_id"], "") ||
      branchLocation?.branch_location_id ||
      branchLocation?.id ||
      "",
    branch_location_name:
      item?.branch_location_name ||
      explicitBranchName ||
      (branchArea && provinceName ? `${branchArea} - ${provinceName}` : branchArea || provinceName || ""),
    source_system:
      item?.source_system ||
      pickWeighbridgeValue(sourceItem, ["source_system", "integration_name"], ""),
    sync_batch_id:
      item?.sync_batch_id ||
      pickWeighbridgeValue(sourceItem, ["sync_batch_id", "probable_sync_batch_id"], ""),
    designation:
      item?.designation ||
      pickWeighbridgeValue(sourceItem, ["designation"], ""),
    raw_payload:
      item?.raw_payload ||
      pickWeighbridgeValue(sourceItem, ["raw_payload"], null),
    weigh_bridge_ticket_status:
      item?.weigh_bridge_ticket_status ||
      ticketStatus?.weigh_bridge_ticket_status ||
      ticket?.weigh_bridge_ticket_status ||
      (typeof sourceItem?.weigh_bridge_ticket_status === "string"
        ? sourceItem.weigh_bridge_ticket_status
        : "") ||
      "",
    weigh_bridge_ticket_status_id:
      item?.weigh_bridge_ticket_status_id ||
      ticket?.weigh_bridge_ticket_status_id ||
      sourceItem?.weigh_bridge_ticket_status_id ||
      ticketStatus?.weigh_bridge_ticket_status_id ||
      "",
    weigh_bridge_ticket_type:
      item?.weigh_bridge_ticket_type ||
      ticketType?.weigh_bride_ticket_type ||
      ticketType?.weigh_bridge_ticket_type ||
      ticket?.weigh_bridge_ticket_type ||
      (typeof sourceItem?.weigh_bridge_ticket_type === "string"
        ? sourceItem.weigh_bridge_ticket_type
        : "") ||
      "",
    weigh_bridge_ticket_type_id:
      item?.weigh_bridge_ticket_type_id ||
      ticket?.weigh_bridge_ticket_type_id ||
      sourceItem?.weigh_bridge_ticket_type_id ||
      ticketType?.weigh_bridge_ticket_type_id ||
      "",
    usage_status: item?.usage_status || resolveWeighbridgeUsageStatus(sourceItem),
    weighbridge_ticket_data: item,
  };
};

export const resolveWeighbridgeUsageStatus = (item = {}) => {
  const explicitUsageCandidates = [
    pickWeighbridgeValue(item, ["usage_status"], ""),
    pickWeighbridgeValue(item, ["used_status"], ""),
    pickWeighbridgeValue(item, ["ticket_usage_status"], ""),
    pickWeighbridgeValue(item, ["weigh_bridge_ticket_usage_status"], ""),
  ];

  const resolvedExplicitUsage = explicitUsageCandidates.find(
    (value) => typeof value === "string" && value.trim(),
  );

  if (resolvedExplicitUsage) {
    return resolvedExplicitUsage.trim().toUpperCase();
  }

  const booleanUsageCandidates = [
    pickWeighbridgeValue(item, ["is_used"], null),
    pickWeighbridgeValue(item, ["used"], null),
  ];

  const resolvedBooleanUsage = booleanUsageCandidates.find(
    (value) => typeof value === "boolean",
  );

  if (typeof resolvedBooleanUsage === "boolean") {
    return resolvedBooleanUsage ? "USED" : "UNUSED";
  }

  const linkedDocumentCandidates = [
    pickWeighbridgeValue(item, ["goods_received_id"], ""),
    pickWeighbridgeValue(item, ["goods_received_ref_number"], ""),
    pickWeighbridgeValue(item, ["delivery_note_id"], ""),
    pickWeighbridgeValue(item, ["delivery_note_ref_number"], ""),
    pickWeighbridgeValue(item, ["loading_order_id"], ""),
    pickWeighbridgeValue(item, ["loading_order_ref_no"], ""),
  ];

  if (linkedDocumentCandidates.some((value) => isPresent(value))) {
    return "USED";
  }

  const normalizedDesignation = normalizeWeighbridgeText(
    pickWeighbridgeValue(item, ["designation"], ""),
  );

  if (!normalizedDesignation || normalizedDesignation === "not set") {
    return "UNUSED";
  }

  return "USED";
};

const normalizeWeighbridgeSortDate = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

const normalizeWeighbridgeSortNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return Number.NEGATIVE_INFINITY;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? Number.NEGATIVE_INFINITY : parsedValue;
};

export const sortWeighbridgeTicketsDesc = (tickets = []) => {
  return [...tickets].sort((leftTicket, rightTicket) => {
    const rightTimestamp = normalizeWeighbridgeSortDate(
      rightTicket?.created_on || rightTicket?.time_in || rightTicket?.inserted_at,
    );
    const leftTimestamp = normalizeWeighbridgeSortDate(
      leftTicket?.created_on || leftTicket?.time_in || leftTicket?.inserted_at,
    );

    if (rightTimestamp !== leftTimestamp) {
      return rightTimestamp - leftTimestamp;
    }

    const rightTicketNumber = normalizeWeighbridgeSortNumber(
      rightTicket?.weigh_bridge_ticket_number || rightTicket?.weighbridge_ticket_id || rightTicket?.id,
    );
    const leftTicketNumber = normalizeWeighbridgeSortNumber(
      leftTicket?.weigh_bridge_ticket_number || leftTicket?.weighbridge_ticket_id || leftTicket?.id,
    );

    if (rightTicketNumber !== leftTicketNumber) {
      return rightTicketNumber - leftTicketNumber;
    }

    return String(rightTicket?.weigh_bridge_ticket_reference_no || "").localeCompare(
      String(leftTicket?.weigh_bridge_ticket_reference_no || ""),
    );
  });
};
